import { Fragment, type ReactNode } from 'react'
import type { JiraConnectionInfo } from '../api'

// Atlassian JIRA project keys are uppercase letters/digits then a dash then a number.
// Source pattern only — callers create their own RegExp instance to avoid the
// shared-lastIndex footgun if anyone ever switches to .exec/.test on a global regex.
export const JIRA_KEY_PATTERN = '\\b([A-Z][A-Z0-9_]*-\\d+)\\b'

/**
 * Pull the user's connected JIRA base URL from jiraStatus. Prefers the first
 * active multi-instance entry; falls back to the legacy single-instance
 * jiraCloudUrl. Returns null when no JIRA is connected, when the URL is missing,
 * or when the URL is not an https Atlassian-style host — defense-in-depth so a
 * tampered or malformed value cannot produce a javascript:/data: link.
 */
export function resolveJiraBaseUrl(status: JiraConnectionInfo | null): string | null {
  if (!status || !status.connected) return null
  const activeInstance = status.instances?.find((i) => i.active)
  const raw = activeInstance?.cloudUrl ?? status.jiraCloudUrl
  if (!raw) return null
  let parsed: URL
  try {
    parsed = new URL(raw)
  } catch {
    return null
  }
  if (parsed.protocol !== 'https:') return null
  // Allow Atlassian Cloud and self-hosted JIRA. Tighten further if needed by
  // checking parsed.hostname.endsWith('.atlassian.net').
  return `${parsed.origin}${parsed.pathname.replace(/\/+$/, '')}`
}

/**
 * Splits text on JIRA-key patterns and renders each match as an anchor pointing
 * at the user's connected JIRA instance. Returns a ReactNode in every branch so
 * callers can splat the result into JSX without runtime type checks.
 */
export function linkifyJiraKeys(text: string, baseUrl: string | null): ReactNode {
  if (!text) return text
  if (!baseUrl) return text
  // Fresh regex per call so a future caller switching to .exec/.test does not
  // share lastIndex with a module-level instance.
  const regex = new RegExp(JIRA_KEY_PATTERN, 'g')
  const parts = text.split(regex)
  return parts.map((part, i) => {
    // Capture groups land on odd indices when splitting with a single capture.
    if (i % 2 === 1) {
      return (
        <a
          key={`${part}-${i}`}
          href={`${baseUrl}/browse/${part}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${part} in JIRA`}
          onClick={(e) => e.stopPropagation()}
          className="text-linear-accent hover:underline"
        >
          {part}
        </a>
      )
    }
    return part ? <Fragment key={`text-${i}`}>{part}</Fragment> : null
  })
}
